import {Item} from '@PosterWhiteboard/items/item/item.class';
import type {BaseItemObject, CopyableItemStyles, InitItemOpts, ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE, ItemLoadingProgressType} from '@PosterWhiteboard/items/item/item.types';
import type {RGBA} from '@Utils/color.util';
import {convertFabricColorToFillObject, rgbaToHex} from '@Utils/color.util';
import {convertFillObjectToFabricColor} from "@Utils/fabric.util";
import {getDefaultFontSizeForFontFamily, getFancyTextEffectForId, getFancyTextUrl, MorphType} from '@Libraries/fancy-text-library';
import {degToRad} from '@Utils/math.util';
import {openPosterEditorEditFancyTextModal} from '@Modals/poster-editor-fancy-text-modal';
import {loadImageAsync} from '@Utils/image.util';
import {FabricImage, type SerializedShadowOptions} from '@postermywall/fabricjs-2';
import type {DeepPartial} from '@/global';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {startItemLoading, removeItemLoading} from '@Libraries/item-loading.library';

const SCREEN_SIZE_SCALE = 0.7;
/**
 * Min scale for highres. Equal to the screen size scale
 * as image for that would be cached and loaded instantly
 */
const MIN_HR_SCALE = SCREEN_SIZE_SCALE;
const MAX_HR_SCALE = 2.2;
const VERSION_2 = 2;
const DEFAULT_HEX = '000000';
const MAX_COLORS_AMOUNT = 3;

export interface FancyTextItemObject extends BaseItemObject {
  idFancyText: string;
  text: string;
  fontFamily: string;
  colors: RGBA[];
  morphType: MorphType;
  morphAmount: number;
}

export class FancyTextItem extends Item {
  declare _fabricObject: FabricImage;
  public idFancyText!: string;
  public text!: string;
  public fontFamily!: string;
  public colors: RGBA[] = [];
  public morphType = MorphType.NONE;
  public morphAmount = 0;
  public gitype = ITEM_TYPE.FANCY_TEXT;
  public override version = VERSION_2;
  public imageElement!: HTMLImageElement;

  override get fabricObject(): FabricImage {
    return this._fabricObject;
  }

  protected override async fixChanges(applyVersionFixesData: Record<string, any>): Promise<void> {
    await super.fixChanges(applyVersionFixesData);
    this.applyFixForVersion2();
  }

  public override toObject(): FancyTextItemObject {
    return {
      ...super.toObject(),
      idFancyText: this.idFancyText,
      text: this.text,
      fontFamily: this.fontFamily,
      colors: this.colors,
      morphType: this.morphType,
      morphAmount: this.morphAmount,
    };
  }

  protected override async reInitFabricObject(): Promise<void> {
    startItemLoading(this, {
      progress: {
        type: ItemLoadingProgressType.FAKE,
        expectedTotalTime: 1,
      },
    });
    try {
      const totalDesiredWidth = this.fabricObject.width * this.fabricObject.scaleX;
      await super.reInitFabricObject();
      const newScale = totalDesiredWidth / this.imageElement.width;
      this.fabricObject.set({
        scaleX: newScale,
        scaleY: newScale,
      });
    } finally {
      removeItemLoading(this);
    }
  }

  public override getCopyableStyles(): CopyableItemStyles {
    return {
      ...super.getCopyableStyles(),
      fontFamily: this.fontFamily,
      colorsFancyText: this.colors,
      idFancyText: this.idFancyText,
      morphType: this.morphType,
      morphAmount: this.morphAmount,
      frontFill: convertFillObjectToFabricColor({
        fillType: this.colors.length > 1 ? FillTypes.LINEAR_GRADIENT : FillTypes.SOLID,
        fillColor: [this.colors[0], this.colors[1]],
      }),
      backFill: {
        fillType: FillTypes.NONE,
        fillColor: [[0, 0, 0, 1]],
      },
    };
  }

  public override async pasteStyles(copiedStyles: CopyableItemStyles): Promise<void> {
    const stylesObj: DeepPartial<FancyTextItemObject> = {};
    if (copiedStyles.fontFamily !== undefined) {
      stylesObj.fontFamily = copiedStyles.fontFamily;
    }
    if (copiedStyles.colorsFancyText !== undefined) {
      stylesObj.colors = copiedStyles.colorsFancyText;
    }
    if (copiedStyles.idFancyText !== undefined) {
      stylesObj.idFancyText = copiedStyles.idFancyText;
    }
    if (copiedStyles.morphType !== undefined) {
      stylesObj.morphType = copiedStyles.morphType;
    }
    if (copiedStyles.morphAmount !== undefined) {
      stylesObj.morphAmount = copiedStyles.morphAmount;
    }

    if (copiedStyles.frontFill !== undefined) {
      const newColorsArray: RGBA[] = [];
      const fillObject = convertFabricColorToFillObject(copiedStyles.frontFill);
      for (let i = 0; i < this.colors.length; i++) {
        newColorsArray[i] = fillObject.fillColor[i] ?? this.colors[i];
      }
      stylesObj.colors = newColorsArray;
    }

    const commonStylesObj: DeepPartial<ItemObject> = this.createCommonStylesObject(copiedStyles, ['border', 'scaleX', 'scaleY']);
    await this.updateFromObject({...commonStylesObj, ...stylesObj});
  }

  public override async formatItemObjectWithDefaultValues(obj: DeepPartial<FancyTextItemObject>): Promise<DeepPartial<FancyTextItemObject>> {
    if (!obj.idFancyText) {
      throw new Error('no idFancyText for new fancy text item');
    }

    const fancyTextEffect = await getFancyTextEffectForId(obj.idFancyText);
    return {
      colors: fancyTextEffect.defaultColors,
      fontFamily: fancyTextEffect.defaultFamilyName,
      ...obj,
    };
  }

  protected override itemObjectHasDestructiveChanges(oldItemObject: FancyTextItemObject): boolean {
    return (
      oldItemObject.idFancyText !== this.idFancyText ||
      oldItemObject.text !== this.text ||
      oldItemObject.fontFamily !== this.fontFamily ||
      oldItemObject.colors !== this.colors ||
      oldItemObject.morphType !== this.morphType ||
      oldItemObject.morphAmount !== this.morphAmount
    );
  }

  protected async getFabricObjectForItem(opts: InitItemOpts = {}): Promise<FabricImage> {
    this.imageElement = await loadImageAsync(this.getImageUrl(this.getScale()));

    const fabricOpts: Record<string, unknown> = {};
    let newScale: number | undefined;

    if (opts.width !== undefined && opts.scaleX !== undefined) {
      const totalDesiredWidth = opts.width * opts.scaleX;
      newScale = totalDesiredWidth / this.imageElement.width;
      fabricOpts.scaleX = newScale;
      fabricOpts.scaleY = newScale;
    }

    if (opts.shadow) {
      fabricOpts.shadow = newScale !== undefined ? this.scaleShadow(opts.shadow, newScale, newScale, opts.scaleX, opts.scaleY) : opts.shadow;
    }

    return new FabricImage(this.imageElement, fabricOpts);
  }

  private getScale(): number {
    if (this.page.poster.isHighRes) {
      return MAX_HR_SCALE;
    }

    return SCREEN_SIZE_SCALE;
  }

  private applyFixForVersion2(): void {
    if (this.version < VERSION_2) {
      const changeInHeightForVersion2 = 8;
      const deltaY = changeInHeightForVersion2 * this.fabricObject.scaleY;
      const cosOfAngle = Math.cos(degToRad(this.fabricObject.angle));
      const sinOfAngle = Math.sin(degToRad(this.fabricObject.angle));

      this.fabricObject.set({
        left: this.fabricObject.left + sinOfAngle * deltaY,
        top: this.fabricObject.top - cosOfAngle * deltaY,
      });
      this.version = VERSION_2;
    }
  }

  private getImageUrl(scale: number): string {
    return getFancyTextUrl(
      this.idFancyText,
      this.fontFamily,
      getDefaultFontSizeForFontFamily(this.fontFamily),
      scale,
      this.text,
      this.getColorHexes(),
      this.morphType,
      this.morphAmount,
      this.version
    );
  }

  private getColorHexes(): string[] {
    const colorHexes = [];
    for (const color of this.colors) {
      colorHexes.push(rgbaToHex(color, false));
    }

    const lengthOfColorHexes = colorHexes.length;

    for (let index = 0; index < MAX_COLORS_AMOUNT - lengthOfColorHexes; index += 1) {
      colorHexes.push(DEFAULT_HEX);
    }

    return colorHexes;
  }

  protected override onItemDoubleTapped(): void {
    openPosterEditorEditFancyTextModal();
  }

  public updateFancyTextEffect(id: string, colors: RGBA[]): Promise<void> {
    return this.updateFromObject({
      idFancyText: id,
      colors,
    });
  }

  public updateFancyTextFont(fontFamily: string): Promise<void> {
    return this.updateFromObject({
      fontFamily,
    });
  }

  public updateTextContent(value: string, undoable = true): void {
    void this.updateFromObject(
      {
        text: value,
      },
      {
        undoable,
      }
    );
  }

  public isMorphAmountApplicable(): boolean {
    return this.morphType === MorphType.CURVE_UP || this.morphType === MorphType.CURVE_DOWN || this.morphType === MorphType.WAVE;
  }
}
